import React, {FC, Fragment, useCallback, useEffect, useMemo} from "react";
import Icon from "../common/icon";

import styles from "./fixtureEditorAngle.module.scss";
import Dropdown from "../common/dropdown";
import {AngleAudio} from "../../store/editorAngles/types";
import useEditorAngles from "../../store/editorAngles/useEditorAngles";
import {toNumber} from "lodash";

export interface FixtureEditorAngleProps {
    angleId: string;
    elementKey: string;
    label: string;
    onClickOption: (angleId: string) => void;
    audioOptions: any[]
}


export interface FixtureTrack {
    id: number;
    default: boolean;
    forced: boolean;
    name: string;
}


const FixtureEditorAngle: FC<FixtureEditorAngleProps> = ({angleId, label, onClickOption, audioOptions, elementKey}) => {
    const {anglesAudio, setAnglesAudio } = useEditorAngles();

    const audioSelected = useMemo(() => {
        const currentAudio = anglesAudio.find((angle) => angle.angleId.toString() === elementKey.toString())?.audioTrackId;
        return audioOptions.find((option => option.id === currentAudio));
    },[elementKey, audioOptions, anglesAudio]);

    const upsertAngleAudio = useCallback((newAngle: AngleAudio) => {
        setAnglesAudio((prevAngles) => {
            const index = prevAngles.findIndex(angle => angle.angleId === newAngle.angleId);
            if (index === -1) {
                return [...prevAngles, newAngle];
            } else {
                const updated = [...prevAngles];
                updated[index] = newAngle;
                return updated;
            }
        });
    }, [setAnglesAudio]);

    const newAudioForAngle = useCallback((trackId: number) => {
        return {
            angleId: elementKey,
            commonAngleId: angleId,
            audioTrackId: trackId,
        } as unknown as AngleAudio;
    }, [angleId, elementKey]);

    const getAudioTrackFromName = useCallback((name: string): FixtureTrack | undefined => {
        return audioOptions.find((option) => option.name === name);
    }, [audioOptions]);

    const selectAudioOption = useCallback((e) => {
        const target = e.target as HTMLElement;
        const selectedName = target.id;
        const selectedTrack = getAudioTrackFromName(selectedName);

        if (selectedTrack && selectedTrack.id !== audioSelected?.id) {
            upsertAngleAudio(newAudioForAngle(toNumber(selectedTrack.id)));
        }
    }, [getAudioTrackFromName, newAudioForAngle, audioSelected, upsertAngleAudio]);


    useEffect(() => {
        if (!audioOptions || audioOptions.length === 0) return;

        const existingAudio = anglesAudio.find((angle) => angle.angleId.toString() === elementKey);

        if (existingAudio){
            return;
        }

        const defaultOption = audioOptions.find(option => option.name.toLowerCase() === "comms");
        const optionToSelect = defaultOption ?? audioOptions[0];
        upsertAngleAudio(newAudioForAngle(toNumber(optionToSelect.id)));
    }, [elementKey, audioOptions, anglesAudio, upsertAngleAudio, newAudioForAngle]);

    const dropdownOptions = useMemo(() => {
        return audioOptions
            .filter((option) => option.name !== audioSelected?.name)
            .map((option) => option.name);
    }, [audioOptions, audioSelected]);


    return (
        <Fragment>
            <div id={elementKey} className={styles.fixtureEditorAngle}>
                <div className={styles.fixtureHeading}>
                    <div className={styles.label}>{label}</div>
                        <div className={styles.audioOptions}>
                            <Dropdown
                                id={`audio-source-options-${elementKey}`}
                                classes={` ${styles.button}`}
                                onClick={selectAudioOption}
                                name={audioSelected?.name}
                                rippleColor="white"
                                onOpen={() => {}}
                                onClose={() => {}}
                                tooltip="Audio Source"
                                suggestions={dropdownOptions}
                            />
                        </div>
                    <div onClick={() => onClickOption(elementKey)} className={styles.removeAngle}>
                        <Icon icon={{icon: ["fas", "times"], type: "FontAwesomeIcon"}}/>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default FixtureEditorAngle;
